import { ColumnDef } from "@tanstack/react-table";
import { Button } from "../components/ui/button";
import {
  ArrowUpDown,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  DollarSign,
  Users,
  AlertTriangle,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ConfirmationDialog } from "../components/confirmationDialog";
import axios from "axios";

export type Project = {
  id: string;
  projectName: string;
  clientId: {
    _id: string;
    clientName: string;
    contactPerson: {
      name: string;
      email: string;
    };
  };
  status: "planning" | "in_progress" | "on_hold" | "completed" | "cancelled";
  priority: "low" | "medium" | "high" | "critical";
  timeline: {
    startDate: string;
    estimatedEndDate: string;
    actualEndDate?: string;
  };
  paymentStructure: {
    totalAmount: number;
    totalPaid: number;
    remainingAmount: number;
    currency: string;
  };
  progress?: {
    completionPercentage: number;
  };
  hasInactiveEmployees?: boolean;
  inactiveEmployeesCount?: number;
  createdAt: string;
};

// Status badge component
const StatusBadge = ({ status }: { status: Project["status"] }) => {
  const getStatusStyles = (status: string) => {
    switch (status) {
      case "planning":
        return "bg-blue-100 text-blue-800 border border-blue-200";
      case "in_progress":
        return "bg-green-100 text-green-800 border border-green-200";
      case "on_hold":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "completed":
        return "bg-purple-100 text-purple-800 border border-purple-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "on_hold":
        return "On Hold";
      case "in_progress":
        return "In Progress";
      default:
        return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getStatusStyles(
        status
      )}`}
    >
      {getStatusText(status)}
    </span>
  );
};

// Priority badge component
const PriorityBadge = ({ priority }: { priority: Project["priority"] }) => {
  const getPriorityStyles = (priority: string) => {
    switch (priority) {
      case "critical":
        return "bg-red-100 text-red-800 border border-red-200";
      case "high":
        return "bg-orange-100 text-orange-800 border border-orange-200";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border border-yellow-200";
      case "low":
        return "bg-green-100 text-green-800 border border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border border-gray-200";
    }
  };

  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium ${getPriorityStyles(
        priority
      )}`}
    >
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </span>
  );
};

// Delete project function
const deleteProject = async (projectId: string) => {
  const token = localStorage.getItem("authToken");
  await axios.delete(
    `${process.env.REACT_APP_BACKEND_URL}api/projects/${projectId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

// Actions cell component
const ActionsCell = ({
  project,
  onProjectDeleted,
}: {
  project: Project;
  onProjectDeleted: () => void;
}) => {
  const navigate = useNavigate();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleView = () => {
    navigate(`/projects/${project.id}`);
  };

  const handleEdit = () => {
    navigate(`/projects/edit/${project.id}`);
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteProject(project.id);
      onProjectDeleted();
      setIsDeleteDialogOpen(false);
    } catch (error: any) {
      console.error("Failed to delete project:", error);
      alert(
        error.response?.data?.message ||
          "Failed to delete project. Please try again."
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuItem onClick={handleView}>
            <Eye className="mr-2 h-4 w-4" />
            View Details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleEdit}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Project
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setIsDeleteDialogOpen(true)}
            className="text-red-600"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Project
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        title="Delete Project"
        description={`Are you sure you want to delete "${project.projectName}"? This action cannot be undone.`}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export const createProjectColumns = (
  onProjectDeleted: () => void
): ColumnDef<Project>[] => [
  {
    accessorKey: "projectName",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          className="h-8 px-2 lg:px-3"
        >
          Project Name
          <ArrowUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: ({ row }) => {
      const project = row.original;
      return (
        <div className="flex flex-col">
          <span className="font-medium">{project.projectName}</span>
          <span className="text-sm text-muted-foreground">
            {project.clientId.clientName}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => {
      return <StatusBadge status={row.getValue("status")} />;
    },
  },
  {
    accessorKey: "priority",
    header: "Priority",
    cell: ({ row }) => {
      return <PriorityBadge priority={row.getValue("priority")} />;
    },
  },
  {
    id: "employeeStatus",
    header: "Team Status",
    cell: ({ row }) => {
      const project = row.original;
      if (project.hasInactiveEmployees) {
        return (
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
              <AlertTriangle className="mr-1 h-3 w-3" />
              {project.inactiveEmployeesCount} Inactive
            </span>
          </div>
        );
      }
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-green-100 text-green-800 border border-green-200">
          <Users className="mr-1 h-3 w-3" />
          All Active
        </span>
      );
    },
  },
  {
    accessorKey: "timeline",
    header: "Timeline",
    cell: ({ row }) => {
      const timeline = row.getValue("timeline") as Project["timeline"];
      const startDate = new Date(timeline.startDate);
      const endDate = timeline.actualEndDate
        ? new Date(timeline.actualEndDate)
        : new Date(timeline.estimatedEndDate);

      return (
        <div className="flex flex-col text-sm">
          <span>{startDate.toLocaleDateString()}</span>
          <span className="text-muted-foreground">
            to {endDate.toLocaleDateString()}
          </span>
        </div>
      );
    },
  },
  {
    id: "payment",
    header: "Payment",
    cell: ({ row }) => {
      const payment = row.original.paymentStructure;
      const percentPaid = (payment.totalPaid / payment.totalAmount) * 100;

      return (
        <div className="flex flex-col">
          <span className="font-medium">
            {payment.currency} {payment.totalPaid.toLocaleString()} /{" "}
            {payment.totalAmount.toLocaleString()}
          </span>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500"
                style={{ width: `${percentPaid}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground">
              {percentPaid.toFixed(0)}%
            </span>
          </div>
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      const project = row.original;
      return (
        <ActionsCell project={project} onProjectDeleted={onProjectDeleted} />
      );
    },
  },
];
